import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { conferenceAPI, getImageUrl, handleImageError } from '../services/api';
import { Calendar, MapPin, Ticket, ExternalLink, Loader2, Globe, Clock } from 'lucide-react';
import SocialShare from '../components/SocialShare';
import SEO, { conferenceJsonLd } from '../components/SEO';

const ConferenceDetail = () => {
  const { slug } = useParams();
  const [conference, setConference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConference();
  }, [slug]);

  const fetchConference = async () => {
    try {
      const response = await conferenceAPI.getById(slug);
      setConference(response.data);
    } catch (err) {
      console.error('Error fetching conference:', err);
      setError('Conference not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const addToGoogleCalendar = () => {
    if (!conference) return;
    
    const startDate = new Date(conference.date).toISOString().replace(/-|:|\.\d+/g, '');
    const endDate = new Date(new Date(conference.date).getTime() + 8 * 60 * 60 * 1000).toISOString().replace(/-|:|\.\d+/g, '');
    
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(conference.title)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(conference.description)}&location=${encodeURIComponent(conference.location)}`;
    
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="container mx-auto px-4 py-16 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  if (error || !conference) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Conference Not Found
          </h1>
          <Link to="/">
            <Button variant="outline">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pageUrl = `${window.location.origin}/conferences/${conference.slug || slug}`;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title={conference.title}
        description={conference.description?.substring(0, 160)}
        canonical={pageUrl}
        type="event"
        image={getImageUrl(conference.coverImage)}
        jsonLd={conferenceJsonLd(conference, pageUrl)}
      />
      <Header />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-5xl mx-auto">
          {/* Back button */}
          <Link
            to="/"
            className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 mb-8 transition-colors"
          >
            ← Back to all conferences
          </Link>

          {/* Hero image */}
          <div className="relative h-96 rounded-2xl overflow-hidden mb-8">
            <img
              src={getImageUrl(conference.coverImage)}
              alt={conference.title}
              onError={handleImageError}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-6 left-6 flex flex-wrap gap-2">
              {conference.categories.map((cat, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 text-sm px-4 py-2"
                >
                  {cat}
                </Badge>
              ))}
            </div>
            <div className="absolute top-6 right-6">
              <Badge
                variant="secondary"
                className="bg-purple-600 text-white text-sm px-4 py-2"
              >
                {conference.format}
              </Badge>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                {conference.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 mb-8 text-gray-600 dark:text-gray-400">
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {formatDate(conference.date)}
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  {conference.location}
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  {conference.format}
                </div>
              </div>

              {/* About section */}
              <Card className="p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  About this conference
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed whitespace-pre-line">
                  {conference.description}
                </p>
              </Card>

              {/* Speakers section */}
              {conference.speakers && conference.speakers.length > 0 && (
                <Card className="p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Speakers
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {conference.speakers.map((speaker, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 text-sm px-4 py-2"
                      >
                        {speaker}
                      </Badge>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="md:col-span-1">
              <Card className="p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                  Event Details
                </h3>

                <div className="space-y-6">
                  {/* Price */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                      Price
                    </h4>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {conference.priceRange}
                    </p>
                  </div>

                  {/* Format */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                      Format
                    </h4>
                    <p className="text-gray-900 dark:text-white">
                      {conference.format}
                    </p>
                  </div>

                  {/* Location */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                      Location
                    </h4>
                    <p className="text-gray-900 dark:text-white">
                      {conference.location}
                    </p>
                  </div>

                  {/* Website */}
                  {conference.websiteUrl && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                        Website
                      </h4>
                      <a
                        href={conference.websiteUrl.startsWith('http') ? conference.websiteUrl : `https://${conference.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 dark:text-purple-400 hover:underline flex items-center"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        Visit site
                      </a>
                    </div>
                  )}

                  {/* Organizer */}
                  {conference.organizer && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                        Organizer
                      </h4>
                      <p className="text-gray-900 dark:text-white">
                        {conference.organizer}
                      </p>
                    </div>
                  )}

                  {/* CTA Buttons */}
                  <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    {conference.ticketUrl && (
                      <Button
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white h-12 text-lg"
                        onClick={() => window.open(conference.ticketUrl, '_blank')}
                      >
                        <Ticket className="w-5 h-5 mr-2" />
                        Buy Tickets
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      className="w-full h-12"
                      onClick={addToGoogleCalendar}
                    >
                      <Calendar className="w-5 h-5 mr-2" />
                      Add to Calendar
                    </Button>
                  </div>

                  {/* Share */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <SocialShare title={conference.title} url={window.location.href} />
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ConferenceDetail;
